/**
 * The team editor (§13.1, AC-001, ADR-121).
 *
 * It edits the real document — the exact bytes of the newest stored version —
 * and everything it does not understand it writes back untouched.
 *
 * One button: **Save and apply**. The compiler still decides (AC-001, every
 * error at once), a version row is still written and history still records
 * each one — but activation happens in the same press and the daemon
 * hot-reloads before the request returns. The earlier ceremony (draft, then
 * activate, then restart a container) protected a guarantee nobody was using
 * and cost the one everybody needs: pressing save and seeing the team change.
 * Runs already in flight keep the digests they started with, exactly as
 * before — that guarantee lives in the runtime, not in the number of buttons.
 */

import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { Link, useNavigate } from 'react-router';

import type { CredentialSummaryView, ExecutionEnvironmentSummary, HarnessSummary, PackageOption, ProviderConnectionView, WorkflowView } from '../../client/types.js';
import {
  AGENT_TEMPLATES,
  addBlankRoleDraft,
  blankIntegration,
  checkLocally,
  duplicateRoleDraft,
  parseTeamDocument,
  readTeam,
  readStageConfigurationSource,
  rebaseTeamEdit,
  roleFromTemplate,
  serializeTeamDocument,
  setRoleWorkflowMode,
  writeTeam,
  writeStageConfigurationSource,
  type IntegrationEdit,
  type RoleEdit,
  type TeamEdit,
} from '../../logic/team-editor.js';
import { useConsoleMutation, useCredentials, usePackages, useProviderConnections, useSkillContents, useTeamVersionDocument, useTeamVersions, useTeamWorkflow } from '../queries.js';
import { describeSaveFailure, useApplyTeamDocument } from '../apply-team.js';
import { readStageConfigurationEdit, starterStageConfiguration, validateStageConfigurationEdit, writeStageConfigurationEdit, type StageConfigurationEdit } from '../../logic/stage-editor.js';
import { useStore } from '../store-context.js';
import { QueryPhase, Section } from '../components/state.js';
import { Button, Flash, Radio, RadioGroup } from '@primer/react';
import { StageVisualEditor } from '../components/stage-visual-editor.js';
import { ToolAccessEditor } from '../components/tool-access-editor.js';

type SaveState =
  | { readonly kind: 'idle' }
  | { readonly kind: 'saving' }
  | { readonly kind: 'rejected'; readonly title: string; readonly errors: readonly string[] }
  /** Identical to what is already running. Not a refusal, not worth an alarm. */
  | { readonly kind: 'unchanged' }
  /** Live: the daemon rebuilt its runtime before the request returned. */
  | { readonly kind: 'applied'; readonly version: number };

export function TeamEditorScreen({
  initialTeam,
  initiallyExpandedRoleKeys = [],
}: {
  /** Optional preloaded form state for an embedded or server-rendered editor. */
  initialTeam?: TeamEdit;
  /** Draft cards an embedding has already opened before the editor mounts. */
  initiallyExpandedRoleKeys?: readonly string[];
} = {}): ReactNode {
  const store = useStore();
  const versions = useTeamVersions();
  const teamId = store.currentTeamId;

  const [source, setSource] = useState<string | null>(null);
  const [team, setTeam] = useState<TeamEdit | null>(null);
  const [stageSource, setStageSource] = useState('');
  const [loadedStageSource, setLoadedStageSource] = useState('');
  const [stageEdit, setStageEdit] = useState<StageConfigurationEdit | null>(null);
  const [stageParseError, setStageParseError] = useState<string | null>(null);
  const [advancedStages, setAdvancedStages] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  /**
   * Machines an agent can be placed on.
   *
   * Empty until the plane answers, and empty for good on an installation that
   * has no plane — which the placement field reads as "offer a text box", not
   * as "there is nowhere to run". A failure here must not take the screen down:
   * not knowing where an agent *could* run is no reason to stop somebody
   * editing what it does.
   */
  const [environments, setEnvironments] = useState<readonly ExecutionEnvironmentSummary[]>([]);
  /**
   * Agent programs, from the platform rather than from a list in here.
   *
   * The Console used to hold its own and it drifted: two offered, four
   * supported. Empty until the answer arrives, which the picker reads as "show
   * what this role already has and nothing else" rather than as "there is
   * nothing to choose".
   */
  const [harnesses, setHarnesses] = useState<readonly HarnessSummary[]>([]);
  const [save, setSave] = useState<SaveState>({ kind: 'idle' });
  /**
   * Per-stage routing overrides, as the document holds them (schema v11).
   *
   * Seeded when both halves exist: the document (what is overridden) and the
   * workflow view (which stages there are and what the defaults say). Null
   * until then, which the section reads as "still loading".
   */
  const workflow = useTeamWorkflow();
  const packages = usePackages();
  const credentials = useCredentials();
  /** The accounts an agent may be pinned to (ADR-131); empty before they load. */
  const connections = useProviderConnections();
  /** The template picker under «Add an agent», and which card to open expanded. */
  const [picking, setPicking] = useState(false);
  const [justAdded, setJustAdded] = useState<number | null>(null);

  /*
   * The *active* version: the screen edits what is running. Activation can
   * re-attach to an older version when the saved content already existed, so
   * the newest stored version is not always the live one — editing it showed
   * agents that were already removed. Newest is only the fallback for a team
   * that has versions but none active.
   */
  const base = versions.data?.find((version) => version.state === 'ACTIVE') ?? versions.data?.[0] ?? null;
  const document = useTeamVersionDocument(teamId, base?.version ?? null);

  /*
   * The document is loaded once per base version, not on every render, and
   * the editor's own state is seeded from it. Re-reading while somebody is
   * typing would throw their work away on any background refetch.
   */
  useEffect(() => {
    if (teamId === null || base === null || document.data === undefined) return;
    if (document.data === null) {
      setLoadError('This version stored no document source, so there is nothing to edit.');
      return;
    }
    setLoadError(null);
    setSource(document.data);
    const parsed = parseTeamDocument(document.data);
    setTeam(readTeam(parsed));
    const nextStageSource = readStageConfigurationSource(parsed);
    setStageSource(nextStageSource);
    setLoadedStageSource(nextStageSource);
    if (nextStageSource.trim() === '') {
      setStageEdit(null);
      setStageParseError(null);
      return;
    }
    try { setStageEdit(readStageConfigurationEdit(nextStageSource)); setStageParseError(null); }
    catch (error) { setStageEdit(null); setStageParseError(String(error)); }
  }, [teamId, base?.version, document.data]);

  useEffect(() => {
    let cancelled = false;
    void store.data
      .harnesses()
      .then((listed) => {
        if (!cancelled) setHarnesses(listed);
      })
      .catch(() => {
        // The field falls back to what the role already names.
      });
    return () => {
      cancelled = true;
    };
  }, [store]);

  useEffect(() => {
    let cancelled = false;
    void store.data
      .environments()
      .then((listed) => {
        if (!cancelled) setEnvironments(listed);
      })
      .catch(() => {
        // Deliberately silent, and the field falls back to free text. An
        // operator who cannot list machines can still name one.
      });
    return () => {
      cancelled = true;
    };
  }, [store]);

  const apply = useApplyTeamDocument();

  const visibleSource = source ?? document.data ?? null;
  const visibleTeam = team ?? initialTeam ?? (visibleSource === null ? null : readTeam(parseTeamDocument(visibleSource)));
  const validationTeam = useMemo(() => {
    if (visibleTeam === null || visibleSource === null) return visibleTeam;
    try {
      const editedDocument = writeStageConfigurationSource(parseTeamDocument(visibleSource), stageSource);
      const editedTeam = readTeam(editedDocument);
      return {
        ...visibleTeam,
        // Stage-native configuration replaces legacy roles as one atomic edit.
        // Validate the identities that will actually be saved, while retaining
        // unsaved changes from the rest of the structured form.
        roles: Array.isArray(editedDocument.spec?.stages) ? editedTeam.roles : visibleTeam.roles,
        configuredAgentIds: editedTeam.configuredAgentIds,
      };
    } catch {
      // The server compiler reports malformed workflow YAML with exact paths.
      // Keep the rest of the local checks useful while the person is typing.
      return visibleTeam;
    }
  }, [stageSource, visibleSource, visibleTeam]);
  const problems = useMemo(() => [
    ...(validationTeam === null ? [] : checkLocally(validationTeam)),
    ...(stageEdit === null ? (stageSource.trim() === '' ? [] : [stageParseError ?? 'Workflow YAML is invalid.']) : validateStageConfigurationEdit(stageEdit)),
  ], [stageEdit, stageParseError, stageSource, validationTeam]);

  if (teamId === null) {
    return <p className="placeholder-inline">No team is active, so there is nothing to configure.</p>;
  }

  if (loadError !== null) {
    return <p className="placeholder-inline">{loadError}</p>;
  }

  if (visibleTeam === null || visibleSource === null) {
    return <QueryPhase isPending={versions.isPending || document.isPending || visibleTeam === null} error={document.error ?? versions.error} source="team document" rows={6} />;
  }

  const editorTeam = visibleTeam;
  const editorSource = visibleSource;

  const onSave = (): void => {
    setSave({ kind: 'saving' });
    /*
     * Rebase before sending: the running version may have moved on since this
     * tab loaded (another tab, another person). The form's own additions,
     * edits and deletions are computed against what the tab loaded, then
     * replayed onto the running document — so pressing Save never resurrects
     * an old team and never asks anybody to redo their typing.
     */
    void (async () => {
      try {
        const teamId = store.currentTeamId;
        if (teamId === null) throw new Error('no team is active');
        const fresh = await store.data.teamVersions();
        const running = fresh.find((version) => version.state === 'ACTIVE') ?? fresh[0];
        if (running === undefined) throw new Error('this team has no stored version');

        let sendSource = editorSource;
        let sendTeam = editorTeam;
        let sendStageSource = stageSource;
        if (base !== null && running.version !== base.version) {
          const runningSource = await store.data.teamVersionDocument(teamId, running.version);
          if (runningSource === null) throw new Error('the running version stored no document source');
          if (stageSource !== loadedStageSource) {
            setSave({ kind: 'rejected', title: 'Workflow changed elsewhere', errors: ['Reload this page, review the newer workflow, and apply your stage or checklist edits again. Nothing was overwritten.'] });
            return;
          }
          sendTeam = rebaseTeamEdit(editorTeam, readTeam(parseTeamDocument(editorSource)), readTeam(parseTeamDocument(runningSource)));
          sendSource = runningSource;
          sendStageSource = readStageConfigurationSource(parseTeamDocument(runningSource));
        }
        const staged = writeStageConfigurationSource(parseTeamDocument(sendSource), sendStageSource);
        const next = serializeTeamDocument(writeTeam(staged, sendTeam));

        const outcome = await apply(() => next, running.version);
        if (outcome.kind === 'unchanged') {
          setSave({ kind: 'unchanged' });
          return;
        }
        setSource(next);
        setTeam(sendTeam);
        setLoadedStageSource(sendStageSource);
        setSave({ kind: 'applied', version: outcome.version });
      } catch (error: unknown) {
        // The compiler's findings name their fields; anything else names
        // itself. The two must not share a headline.
        const described = describeSaveFailure(error);
        setSave({ kind: 'rejected', title: described.title, errors: described.lines });
      }
    })();
  };

  const updateStages = (next: StageConfigurationEdit): void => {
    if (stageEdit !== null) {
      const previousAgents = new Map(stageEdit.stages.filter((stage) => stage.kind === 'agent').map((stage) => [stage.key, stage.id]));
      const nextAgents = new Map(next.stages.filter((stage) => stage.kind === 'agent').map((stage) => [stage.key, stage.id]));
      const renamed = new Map<string, string>();
      const removed = new Set<string>();
      for (const [key, id] of previousAgents) {
        const nextId = nextAgents.get(key);
        if (nextId === undefined) removed.add(id);
        else if (nextId !== id) renamed.set(id, nextId);
      }
      const oldStageIds = new Set(previousAgents.values());
      const backgroundAgentIds = editorTeam.configuredAgentIds.filter((id) => !oldStageIds.has(id));
      const integrations = editorTeam.integrations
        .map((integration) => ({ ...integration, roles: integration.roles.map((id) => renamed.get(id) ?? id).filter((id) => !removed.has(id)) }))
        .filter((integration) => integration.roles.length > 0);
      setTeam({ ...editorTeam, configuredAgentIds: [...backgroundAgentIds, ...nextAgents.values()].filter(Boolean), integrations });
    }
    setStageEdit(next);
    setStageSource(writeStageConfigurationEdit(next));
    setStageParseError(null);
    setSave({ kind: 'idle' });
  };

  const updateBackgroundRoles = (nextRoles: readonly RoleEdit[]): void => {
    if (stageEdit === null) return;
    const previous = new Map(editorTeam.roles.map((role) => [role.draftKey, role.id]));
    const current = new Map(nextRoles.map((role) => [role.draftKey, role.id]));
    const renamed = new Map<string, string>();
    const removed = new Set<string>();
    for (const [draftKey, id] of previous) {
      const nextId = current.get(draftKey);
      if (nextId === undefined) removed.add(id);
      else if (nextId !== id) renamed.set(id, nextId);
    }
    const nextStages = stageEdit.stages.map((stage) => ({
      ...stage,
      notifications: stage.notifications
        .filter((notification) => !removed.has(notification.backgroundAgent))
        .map((notification) => ({ ...notification, backgroundAgent: renamed.get(notification.backgroundAgent) ?? notification.backgroundAgent })),
    }));
    const nextStageEdit = { ...stageEdit, stages: nextStages, backgroundAgentIds: nextRoles.map((role) => role.id).filter(Boolean) };
    const stageAgentIds = nextStages.filter((stage) => stage.kind === 'agent').map((stage) => stage.id);
    const integrations = editorTeam.integrations
      .map((integration) => ({ ...integration, roles: integration.roles.map((id) => renamed.get(id) ?? id).filter((id) => !removed.has(id)) }))
      .filter((integration) => integration.roles.length > 0);
    setTeam({ ...editorTeam, roles: nextRoles, configuredAgentIds: [...stageAgentIds, ...nextStageEdit.backgroundAgentIds], integrations });
    setStageEdit(nextStageEdit);
    setStageSource(writeStageConfigurationEdit(nextStageEdit));
    setSave({ kind: 'idle' });
  };

  return (
    <div className="stack">
      {stageEdit === null ? null : <StageVisualEditor key={teamId} value={stageEdit} packages={packages.data ?? []} onChange={updateStages} />}
      {stageSource.trim() !== '' ? null : <Section label="Stage workflow" lede={editorTeam.roles.length === 0 ? 'Build a node-based workflow with visual forms.' : 'This team still uses legacy agents. Automatic migration is intentionally disabled so their instructions, models, skills, and integrations cannot be lost.'}>
        {editorTeam.roles.length === 0 ? <Button onClick={() => updateStages(starterStageConfiguration())}>Create stage workflow</Button> : <p className="hint">Create a new stage-native team or migrate this team with an explicit reviewed configuration.</p>}
      </Section>}
      <Section label="Advanced YAML" lede="The visual editor and this YAML edit the same configuration. Use this only for fields the forms do not expose yet.">
        <Button onClick={() => setAdvancedStages((shown) => !shown)}>{advancedStages ? 'Hide YAML' : 'Edit YAML'}</Button>
        {advancedStages ? <>
          <textarea
            className="stage-yaml-editor"
            value={stageSource}
            onChange={(event) => {
              const nextSource = event.target.value;
              setStageSource(nextSource);
              setSave({ kind: 'idle' });
              if (nextSource.trim() === '') {
                setStageEdit(null);
                setStageParseError(null);
                return;
              }
              try { setStageEdit(readStageConfigurationEdit(nextSource)); setStageParseError(null); }
              catch (error) { setStageEdit(null); setStageParseError(error instanceof Error ? error.message : String(error)); }
            }}
            rows={Math.min(34, Math.max(10, stageSource.split('\n').length + 1))}
            spellCheck={false}
            placeholder={'workflow:\n  initialStage: development\n  cancelledStage: cancelled\nstages: []\nchecklists: []'}
            aria-label="Stage and checklist configuration"
          />
          {stageParseError === null ? null : <Flash variant="danger" role="alert">{stageParseError}</Flash>}
          <p className="hint">Only workflow, stages, checklists, and backgroundAgents belong in this block.</p>
        </> : null}
      </Section>
      {stageEdit === null ? null : <Section
        label="Background agents"
        lede="Agents that run from chats, webhooks, schedules, or stage-entry notifications without claiming a workflow card."
      >
        <div className="editor-list">
          {editorTeam.roles.map((role, index) => (
            <RoleForm
              role={role}
              rowNumber={index + 1}
              team={editorTeam}
              environments={environments}
              harnesses={harnesses}
              workflow={workflow.data ?? null}
              packages={packages.data ?? []}
              credentials={credentials.data ?? []}
              connections={connections.data ?? []}
              backgroundOnly
              startExpanded={index === justAdded || initiallyExpandedRoleKeys.includes(role.draftKey)}
              key={role.draftKey}
              onChange={(next) => updateBackgroundRoles(editorTeam.roles.map((entry, at) => at === index ? { ...next, workflowMode: 'background' } : entry))}
              onDuplicate={() => updateBackgroundRoles(duplicateRoleDraft(editorTeam, index).roles.map((entry) => ({ ...entry, workflowMode: 'background' })))}
              onToggleConnection={(integrationId) => setTeam({ ...editorTeam, integrations: editorTeam.integrations.map((integration) => integration.id === integrationId ? { ...integration, roles: integration.roles.includes(role.id) ? integration.roles.filter((name) => name !== role.id) : [...integration.roles, role.id] } : integration) })}
              onIntegrationsChange={(next) => setTeam({ ...editorTeam, integrations: next })}
              onRemove={() => updateBackgroundRoles(editorTeam.roles.filter((_, at) => at !== index))}
            />
          ))}
        </div>
        <Button onClick={() => {
          const added = addBlankRoleDraft(editorTeam);
          const roles = added.roles.map((entry) => ({ ...entry, workflowMode: 'background' as const }));
          setJustAdded(roles.length - 1);
          updateBackgroundRoles(roles);
        }}>Add background agent</Button>
      </Section>}
      {stageSource.trim() === '' ? <Section
        label="Agents"
        lede="Who exists, what each one runs on, and where. Saving applies immediately; work already in flight finishes on the configuration it started with."
      >
        <div className="editor-list">
          {editorTeam.roles.map((role, index) => (
            <RoleForm
              role={role}
              rowNumber={index + 1}
              team={editorTeam}
              environments={environments}
              harnesses={harnesses}
              workflow={workflow.data ?? null}
              packages={packages.data ?? []}
              credentials={credentials.data ?? []}
              connections={connections.data ?? []}
              startExpanded={index === justAdded || initiallyExpandedRoleKeys.includes(role.draftKey)}
              key={role.draftKey}
              onChange={(next) =>
                setTeam({ ...editorTeam, roles: editorTeam.roles.map((entry, at) => (at === index ? next : entry)) })
              }
              onDuplicate={() => setTeam(duplicateRoleDraft(editorTeam, index))}
              onToggleConnection={(integrationId) =>
                setTeam({
                  ...editorTeam,
                  integrations: editorTeam.integrations.map((integration) =>
                    integration.id === integrationId
                      ? {
                          ...integration,
                          roles: integration.roles.includes(role.id)
                            ? integration.roles.filter((name) => name !== role.id)
                            : [...integration.roles, role.id],
                        }
                      : integration,
                  ),
                })
              }
              onIntegrationsChange={(next) => setTeam({ ...editorTeam, integrations: next })}
              onRemove={() => setTeam({ ...editorTeam, roles: editorTeam.roles.filter((_, at) => at !== index) })}
            />
          ))}
        </div>
        {picking ? (
          <div className="template-grid">
            {AGENT_TEMPLATES.map((template) => (
              <button
                type="button"
                key={template.key}
                className="template-card"
                onClick={() => {
                  const taken = editorTeam.roles.map((entry) => entry.id);
                  const draft = roleFromTemplate(template, editorTeam);
                  const named = taken.includes(draft.id) ? { ...draft, id: duplicateName(draft.id, taken) } : draft;
                  setJustAdded(editorTeam.roles.length);
                  setTeam({ ...editorTeam, roles: [...editorTeam.roles, named] });
                  setPicking(false);
                }}
              >
                <strong>{template.title}</strong>
                <span>{template.blurb}</span>
              </button>
            ))}
            <button
              type="button"
              className="template-card"
              onClick={() => {
                setJustAdded(editorTeam.roles.length);
                setTeam(addBlankRoleDraft(editorTeam));
                setPicking(false);
              }}
            >
              <strong>Blank agent</strong>
              <span>Start from nothing and fill in every field yourself.</span>
            </button>
            <Button onClick={() => setPicking(false)}>
              Cancel
            </Button>
          </div>
        ) : (
          <Button onClick={() => setPicking(true)}>
            Add an agent
          </Button>
        )}
      </Section> : null}

      {/* No «Save» heading over one button: the bar is the save. */}
      <div className="save-bar">
        {problems.length > 0 ? (
          <Flash variant="warning">
            <strong>Fix these first</strong>
            <ul className="finding-list">
              {problems.map((problem) => (
                <li key={problem}>{problem}</li>
              ))}
            </ul>
          </Flash>
        ) : null}

        {save.kind === 'rejected' ? (
          <Flash variant="danger" role="alert">
            <strong>{save.title}</strong>
            <ul className="finding-list">
              {save.errors.map((error) => (
                <li key={error}>{error}</li>
              ))}
            </ul>
          </Flash>
        ) : null}

        {save.kind === 'unchanged' ? <p className="hint">Nothing changed — the team already runs exactly this.</p> : null}

        {save.kind === 'applied' ? (
          <Flash variant="success">
            <strong>Version {save.version} is live.</strong>
            <p>The team runs it now — no restart. Work already in flight finishes on what it started with.</p>
          </Flash>
        ) : null}

        <div className="save-row">
          <Button variant="primary"
            onClick={onSave}
            disabled={problems.length > 0 || save.kind === 'saving'}
          >
            {save.kind === 'saving' ? 'Applying…' : 'Save and apply'}
          </Button>
          <p className="hint save-hint">
            Checked again on the server; every save becomes a numbered version in the history.
          </p>
        </div>
      </div>
    </div>
  );
}

function RoleForm({
  role,
  rowNumber,
  team,
  environments,
  harnesses,
  workflow,
  packages,
  credentials,
  connections,
  onChange,
  onDuplicate,
  onToggleConnection,
  onIntegrationsChange,
  onRemove,
  backgroundOnly = false,
  startExpanded = false,
}: {
  role: RoleEdit;
  rowNumber: number;
  team: TeamEdit;
  environments: readonly ExecutionEnvironmentSummary[];
  harnesses: readonly HarnessSummary[];
  workflow: WorkflowView | null;
  packages: readonly PackageOption[];
  credentials: readonly CredentialSummaryView[];
  connections: readonly ProviderConnectionView[];
  onChange: (role: RoleEdit) => void;
  onDuplicate: () => void;
  onToggleConnection: (integrationId: string) => void;
  onIntegrationsChange: (next: readonly IntegrationEdit[]) => void;
  onRemove: () => void;
  backgroundOnly?: boolean;
  startExpanded?: boolean;
}): ReactNode {
  /*
   * The caveat replaces the hint rather than sitting beside it. "This harness
   * will not get its integrations yet" is the only thing worth reading when it
   * is true, and burying it under a general description is how somebody picks
   * an agent that quietly cannot do its job.
   */
  const chosen = harnesses.find((harness) => harness.id === role.harness) ?? null;

  /*
   * The account this agent runs on, and the reason it is picked *before* the
   * program: an account belongs to one provider, so choosing it answers the
   * next question too. Picking one therefore moves the harness with it —
   * otherwise the pair compiles to `connection.harness_mismatch` and the person
   * has to work out which of two fields they got wrong.
   */
  const boundConnection = connections.find((connection) => connection.name === role.connection) ?? null;
  const usableConnections = connections.filter(
    (connection) =>
      // Subscription homes are node-local. API keys are stored on the control
      // plane and this first release deliberately does not deliver them to a
      // remote node, so offer only combinations the compiler can execute.
      connection.kind === 'subscription'
        ? connection.environment === role.environment.trim()
        : role.environment.trim() === '',
  );

  // The stage this role owns in the running workflow, for placeholders.
  const derived = workflow?.stages.find((stage) => stage.ownerRoles.includes(role.id)) ?? null;
  const columnNames = workflow?.states.map((state) => state.id) ?? [];
  const isBackground = role.workflowMode === 'background';

  const [collapsed, setCollapsed] = useState(!startExpanded);

  return (
    <article className="editor-card">
      {/*
        The whole row opens the agent, and the chevron says so before any
        click. The name stays editable in place; the row actions surface on
        hover, the way a Notion row keeps its furniture out of the way.
      */}
      <header className="editor-card-head" onClick={() => setCollapsed(!collapsed)}>
        <button
          type="button"
          className="disclosure"
          aria-expanded={!collapsed}
          aria-label={collapsed ? `show details of ${role.id}` : `hide details of ${role.id}`}
          onClick={(event) => {
            event.stopPropagation();
            setCollapsed(!collapsed);
          }}
        >
          <svg viewBox="0 0 16 16" width="14" height="14" aria-hidden>
            <path d="M6 3.6 10.4 8 6 12.4" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
        <input
          className="editor-title"
          value={role.id}
          placeholder="agent name"
          onClick={(event) => event.stopPropagation()}
          onChange={(event) => onChange({ ...role, id: event.target.value })}
        />
        <span
          className="editor-card-actions"
          onClick={(event) => event.stopPropagation()}
        >
          <OpenAgentChat roleId={role.id} />
          {/* A second reviewer is this button, not a package ref somebody types. */}
          <Button onClick={onDuplicate}>
            Duplicate
          </Button>
          <Button variant="danger" onClick={onRemove}>
            Remove
          </Button>
        </span>
      </header>

      {collapsed ? null : (
        <>

      <div className="editor-grid">
        {backgroundOnly ? <Field label="How it runs" hint="This agent is invoked explicitly and never claims a workflow card."><span>Background agent</span></Field> : <TeamEditorWorkflowMode role={role} rowNumber={rowNumber} onChange={onChange} />}

        {/*
          Where this agent stands on the board: which column it takes work
          from and which one the work sits in while it runs. Grey placeholders
          show what the running workflow does today; typing makes it this
          team's own, and a new column is created by typing its name.
        */}
        {isBackground ? null : (
          <>
            <ColumnField label="Takes from" list={columnNames} value={role.colFrom} placeholder={derived?.from ?? 'todo'} onChange={(value) => onChange({ ...role, colFrom: value })} />
            <ColumnField label="Works in" list={columnNames} value={role.colWorking} placeholder={derived?.working ?? 'auto'} onChange={(value) => onChange({ ...role, colWorking: value })} />
          </>
        )}

        {/*
          The account, first, because it decides the two fields under it: an
          account belongs to one provider, and a subscription belongs to one
          machine (ADR-131). Leaving it unset is allowed and says what that
          means, because every team written before accounts existed is in
          exactly that state and still runs.
        */}
        <Field
          label="Account"
          hint={
            boundConnection === null
              ? role.connection.length > 0
                ? `“${role.connection}” is not a connected account — add it under AI providers, or pick another.`
                : 'Not chosen: this agent runs on whatever account its machine happens to be signed in as. Pick one to pin it.'
              : boundConnection.status.state === 'unusable'
                ? `Last check: ${boundConnection.status.detail ?? 'not usable'}`
                : `${boundConnection.provider} · ${boundConnection.kind === 'api' ? 'API key' : 'signed-in subscription'}`
          }
        >
          <select
            value={role.connection}
            onChange={(event) => {
              const picked = connections.find((connection) => connection.name === event.target.value) ?? null;
              onChange({
                ...role,
                connection: event.target.value,
                // The provider comes with the account; keeping the old harness
                // here is the one combination the compiler always refuses.
                ...(picked === null ? {} : { harness: picked.provider }),
              });
            }}
          >
            <option value="">Not chosen — whatever this machine is signed in as</option>
            {usableConnections.map((connection) => (
              <option key={connection.id} value={connection.name}>
                {connection.name} — {connection.provider}
                {connection.kind === 'api' ? ' (API key)' : ''}
                {connection.status.state === 'unusable' ? ' — not usable' : ''}
              </option>
            ))}
            {/*
              An account the document names but this installation does not have
              still appears rather than being silently swapped for one it does.
            */}
            {role.connection.length > 0 && !connections.some((connection) => connection.name === role.connection) && (
              <option value={role.connection}>{role.connection} (no such account here)</option>
            )}
          </select>
        </Field>

        {/*
          The agent program, and it comes second because the account above
          already decided it whenever one is chosen. The platform does not drive the model — the harness
          holds its own tool loop, its own MCP client and its own credential —
          so this is the choice of what the agent actually *is*.
        */}
        <Field
          label="Agent program"
          hint={
            chosen?.caveat ??
            'What the platform starts when it wakes this agent — Claude Code, Codex. The program brings its own tools and model; sign it in from the agent’s chat.'
          }
        >
          <select
            value={role.harness}
            disabled={boundConnection !== null}
            onChange={(event) => onChange({ ...role, harness: event.target.value })}
          >
            {harnesses.map((harness) => (
              <option key={harness.id} value={harness.id}>
                {harness.label}
                {harness.caveat === null
                  ? ''
                  : harness.caveat.startsWith('not signed in')
                    ? ' — not signed in'
                    : harness.caveat.startsWith('not installed')
                      ? ' — not installed'
                      : ' — not ready'}
              </option>
            ))}
            {/*
              A harness the platform does not list still appears, labelled,
              rather than being silently swapped for one it does. Somebody who
              typed it into the document meant it, and a picker that quietly
              reassigns their agent is worse than one showing an unfamiliar
              name. This is also what the field falls back to before the list
              arrives.
            */}
            {harnesses.every((known) => known.id !== role.harness) && role.harness.length > 0 && (
              <option value={role.harness}>{role.harness} (unknown to this installation)</option>
            )}
          </select>
        </Field>

        <ModelField role={role} models={chosen?.models ?? []} onChange={onChange} />

        <Field
          label="Runs on"
          hint={
            /*
             * When the only choice is this machine, the field says how to get
             * another. A picker with one option and no path to a second is a
             * dead end, and the path is a terminal command on a host — pairing
             * machines belongs to Orca (ADR-120) and neither this screen nor
             * the Main Operator can do it. Saying so here is the difference
             * between a limitation and a mystery.
             */
            environments.length <= 1
              ? 'Only this machine is paired. To add a server: run `orca serve --pairing-address <its address>` there, then `orca environment add --name <name> --pairing-code <the code it printed>` here.'
              : 'A machine Orca has been paired with. “This machine” is where the control plane runs.'
          }
        >
          {/*
            A picker when the plane can list machines, a text box when it
            cannot. Not a picker that silently offers nothing: an operator on an
            installation with no plane configured must still be able to place an
            agent, and an empty dropdown reads as "there is nowhere to run".
          */}
          {environments.length === 0 ? (
            <input
              value={role.environment}
              placeholder="this machine"
              onChange={(event) => onChange({ ...role, environment: event.target.value })}
            />
          ) : (
            <select value={role.environment} onChange={(event) => onChange({ ...role, environment: event.target.value })}>
              {environments.map((environment) => (
                <option key={environment.name} value={environment.name}>
                  {environment.name.length === 0 ? 'This machine' : environment.name}
                  {environment.detail === null ? '' : ` — ${environment.detail}`}
                </option>
              ))}
              {environments.every((known) => known.name !== role.environment) && (
                <option value={role.environment}>{role.environment} (not a machine Orca knows)</option>
              )}
            </select>
          )}
        </Field>

        {/*
          Model route, "at once" and the memory policy are gone from this card
          on purpose. The harness brings its own model, an agent holds one task
          at a time, and memory is not a dropdown — it is the agent's own
          folder, which is where a harness keeps its sessions and notes. The
          document still carries the old fields untouched; the card stopped
          asking questions nobody should have to answer.
        */}
      </div>

      <Field
        label="Instructions"
        hint="This agent’s onboarding prompt — who it is, how it works, what its exits mean. Fed as the first message of its session; saved with the team."
      >
        <textarea
          rows={4}
          value={role.instructions}
          placeholder={packages.find((option) => option.ref === role.packageRef)?.goal ?? 'What this agent should do.'}
          onChange={(event) => onChange({ ...role, instructions: event.target.value })}
        />
      </Field>

      {/*
        The exit rules, in order — the agent's whole routing, written by the
        person running the team. Each rule is a condition in your words and
        the column the card moves to when the agent claims it. The agent reads
        the conditions (it is a model; words are its instruction set), claims
        which rule fired, and the platform moves the card — under the same
        fences as everything else. The first rule is the default for an agent
        that finishes silently.
      */}
      {isBackground ? null : (
        <Field
          label="When finished"
          hint="Rules in order: a condition in your words → the column the card moves to. The agent picks the rule that matches; the platform moves the card."
        >
        <div className="stack">
          {role.exits.map((exit, at) => (
            <div className="exit-row" key={at} style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              <input
                style={{ flex: 3 }}
                value={exit.when}
                placeholder="условие — например: тесты зелёные и критерии выполнены"
                onChange={(event) =>
                  onChange({
                    ...role,
                    exits: role.exits.map((entry, index) => (index === at ? { ...entry, when: event.target.value } : entry)),
                  })
                }
              />
              <span aria-hidden>→</span>
              <ColumnInput
                list={columnNames}
                value={exit.to}
                placeholder="колонка"
                onChange={(value) =>
                  onChange({
                    ...role,
                    exits: role.exits.map((entry, index) => (index === at ? { ...entry, to: value } : entry)),
                  })
                }
              />
              <Button
                aria-label="remove this rule"
                onClick={() => onChange({ ...role, exits: role.exits.filter((_, index) => index !== at) })}
              >
                −
              </Button>
            </div>
          ))}
          <Button
            onClick={() => onChange({ ...role, exits: [...role.exits, { when: '', to: '' }] })}
          >
            + add a rule
          </Button>
        </div>
        </Field>
      )}

      <ToolAccessEditor value={role} defaultTools={packages.find((entry) => entry.ref === role.packageRef)?.defaultTools} mcpSources={team.integrations.filter((integration) => integration.type === 'mcp' && integration.roles.includes(role.id)).map((integration) => integration.id)} onChange={onChange} />

      <RoleConnections
        roleId={role.id}
        team={team}
        credentials={credentials}
        onToggle={onToggleConnection}
        onIntegrationsChange={onIntegrationsChange}
      />

      <RoleCredentials roleId={role.id} credentials={credentials} />
      <RoleSkills roleId={role.id} allRoleIds={team.roles.map((entry) => entry.id)} />
        </>
      )}
    </article>
  );
}

/** The per-row board participation control, with an editor-stable native group name. */
export function TeamEditorWorkflowMode({
  role,
  rowNumber,
  onChange,
}: {
  role: RoleEdit;
  rowNumber: number;
  onChange: (role: RoleEdit) => void;
}): ReactNode {
  const accessibleRole = role.id.trim().length > 0 ? `agent ${role.id}` : `draft role ${String(rowNumber)}`;
  return (
    <RadioGroup
      name={`workflow-mode-${role.draftKey}`}
      onChange={(mode) => {
        if (mode === 'background' || mode === 'board-worker') onChange(setRoleWorkflowMode(role, mode));
      }}
    >
      <RadioGroup.Label>Board participation for {accessibleRole}</RadioGroup.Label>
      <label>
        <Radio value="board-worker" checked={role.workflowMode === 'board-worker'} /> Board worker
      </label>
      <label>
        <Radio value="background" checked={role.workflowMode === 'background'} /> Background role
      </label>
      <RadioGroup.Caption>
        Background roles stay available in chat and schedules but do not claim cards or own a workflow stage.
      </RadioGroup.Caption>
    </RadioGroup>
  );
}

/**
 * The agent's connections, on the agent — MCP servers and APIs it can reach.
 *
 * One connection, many readers: the server is declared once and each agent
 * ticks whether it uses it, so three agents sharing one MCP server do not
 * configure it three times. Editing is available from any connected agent's
 * card — the same object, wherever it is opened from.
 */
/**
 * The model, as a picker: the known models of the chosen program, an account
 * default, and an «other…» escape into free text for whatever ships next.
 */
function ModelField({
  role,
  models,
  onChange,
}: {
  role: RoleEdit;
  models: readonly { readonly id: string; readonly label: string }[];
  onChange: (role: RoleEdit) => void;
}): ReactNode {
  const choices = models;
  const known = role.model === '' || choices.some((choice) => choice.id === role.model);
  // Only an explicit «other…» forces the text box; an unknown value shows it on
  // its own (via `known`). Starting false — not `!known` — means a preset model
  // that is still valid resolves to the select the moment the harness list
  // arrives, instead of being stranded in free text because it loaded async.
  const [custom, setCustom] = useState(false);

  return (
    <Field
      label="Model"
      hint="Passed to the program at every start. «Account default» runs whatever the signed-in account is set to."
    >
      <select
        value={custom || !known ? '__custom__' : role.model}
        onChange={(event) => {
          if (event.target.value === '__custom__') {
            setCustom(true);
            return;
          }
          setCustom(false);
          onChange({ ...role, model: event.target.value });
        }}
      >
        <option value="">account default</option>
        {choices.map((choice) => (
          <option key={choice.id} value={choice.id}>
            {choice.label}
          </option>
        ))}
        <option value="__custom__">other…</option>
      </select>
      {custom || !known ? (
        <input
          value={role.model}
          placeholder="exact model id, e.g. claude-opus-4-8"
          onChange={(event) => onChange({ ...role, model: event.target.value })}
        />
      ) : null}
    </Field>
  );
}

/**
 * Straight from the card into the agent's one conversation — where you brief
 * it, watch it think, and set its program up by talking to it.
 */
function OpenAgentChat({ roleId }: { roleId: string }): ReactNode {
  const navigate = useNavigate();
  const open = useConsoleMutation<string, { id: string }>(
    (data, context, id) => data.openRoleChat(id, context),
    [['operator-chats']],
  );
  const usable = roleId.trim().length > 0;
  return (
    <Button
      disabled={!usable || open.isPending}
      title={usable ? `open the chat with ${roleId}` : 'name and save the agent first'}
      onClick={() =>
        open.mutate(roleId, { onSuccess: (chat) => void navigate(`/operator/${chat.id}`) })
      }
    >
      {open.isPending ? 'Opening…' : open.isError ? 'Save the team first' : 'Chat'}
    </Button>
  );
}

function RoleConnections({
  roleId,
  team,
  credentials,
  onToggle,
  onIntegrationsChange,
}: {
  roleId: string;
  team: TeamEdit;
  credentials: readonly CredentialSummaryView[];
  onToggle: (integrationId: string) => void;
  onIntegrationsChange: (next: readonly IntegrationEdit[]) => void;
}): ReactNode {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  return (
    <Field
      label="Connections"
      hint="MCP servers and APIs this agent can reach; it discovers what a server can do by asking it. A connection is one object for the whole team — tick who uses it, edit it from any card."
    >
      <div className="editor-checks">
        {team.integrations.map((integration, index) => {
          const open = openIndex === index;
          return (
            <div key={index} className="skill-row">
              <label className="check">
                <input
                  type="checkbox"
                  checked={integration.roles.includes(roleId)}
                  onChange={() => onToggle(integration.id)}
                />
                {integration.id || '(unnamed connection)'}
                <span className="skill-scope">
                  — {integration.type}
                  {integration.roles.length === 0 ? ', connected to nobody yet' : ''}
                </span>
              </label>
              <span className="skill-actions">
                <Button onClick={() => setOpenIndex(open ? null : index)}>
                  {open ? 'Close' : 'Edit'}
                </Button>
                <Button
                  aria-label={`delete connection ${integration.id}`}
                  onClick={() => {
                    setOpenIndex(null);
                    onIntegrationsChange(team.integrations.filter((_, at) => at !== index));
                  }}
                >
                  Delete
                </Button>
              </span>
              {open ? (
                <div className="skill-edit">
                  <IntegrationForm
                    integration={integration}
                    roles={team.roles.map((entry) => entry.id)}
                    credentials={credentials}
                    onChange={(next) =>
                      onIntegrationsChange(team.integrations.map((entry, at) => (at === index ? next : entry)))
                    }
                    onRemove={() => {
                      setOpenIndex(null);
                      onIntegrationsChange(team.integrations.filter((_, at) => at !== index));
                    }}
                  />
                </div>
              ) : null}
            </div>
          );
        })}
      </div>
      <Button
        onClick={() => {
          onIntegrationsChange([...team.integrations, { ...blankIntegration(), roles: roleId.trim().length === 0 ? [] : [roleId] }]);
          setOpenIndex(team.integrations.length);
        }}
      >
        + add a connection for this agent
      </Button>
    </Field>
  );
}

/**
 * The agent's skills, on the agent — read, scope, edit, add, delete.
 *
 * Texts live in one store; what is per-agent is the *audience*: an unscoped
 * skill reaches everyone (that is what every skill meant before scoping
 * existed), a scoped one reaches exactly the ticked agents. Unticking an
 * unscoped skill therefore materialises «everyone minus this one» from the
 * team's current roster. Files in the homes follow immediately — each turn
 * is a fresh process, so the next message reads the new text.
 */
function RoleSkills({ roleId, allRoleIds }: { roleId: string; allRoleIds: readonly string[] }): ReactNode {
  const skills = useSkillContents();
  const [openRef, setOpenRef] = useState<string | null>(null);
  const [draftText, setDraftText] = useState('');
  const [adding, setAdding] = useState(false);
  const [newName, setNewName] = useState('');
  const [newText, setNewText] = useState('');

  const invalidate = [['skill-contents'], ['skills']] as const;
  const scope = useConsoleMutation<{ ref: string; roles: readonly string[] }, void>(
    (data, context, args) => data.setSkillRoles(args.ref, args.roles, context),
    invalidate,
  );
  const save = useConsoleMutation<{ ref: string; name: string; text: string; enabled?: boolean }, void>(
    (data, context, args) => data.saveSkill(args.ref, args.name, args.text, context, args.enabled),
    invalidate,
  );
  const remove = useConsoleMutation<string, void>((data, context, ref) => data.resetSkill(ref, context), invalidate);

  const usable = roleId.trim().length > 0;
  const list = skills.data ?? [];

  return (
    <Field
      label="Skills"
      hint={
        usable
          ? 'Knowledge this agent reads, as files in its home (skills/*.md). Edits land live — the next message reads the new text. Unticked skills stay with the other agents.'
          : 'Name the agent first — skills are scoped by agent name.'
      }
    >
      <div className="editor-checks">
        {list.map((skill) => {
          const mine = skill.enabled && (skill.roles.length === 0 || skill.roles.includes(roleId));
          const open = openRef === skill.ref;
          return (
            <div key={skill.ref} className="skill-row">
              <label className="check">
                <input
                  type="checkbox"
                  disabled={!usable || scope.isPending || save.isPending}
                  checked={mine}
                  onChange={(event) => {
                    if (event.target.checked) {
                      if (!skill.enabled) {
                        // Turning a dark skill back on: re-enable, scoped to this agent.
                        save.mutate(
                          { ref: skill.ref, name: skill.name, text: skill.text, enabled: true },
                          { onSuccess: () => scope.mutate({ ref: skill.ref, roles: [roleId] }) },
                        );
                        return;
                      }
                      scope.mutate({
                        ref: skill.ref,
                        roles: skill.roles.length === 0 ? [] : [...skill.roles, roleId],
                      });
                      return;
                    }
                    const rest =
                      skill.roles.length === 0
                        ? allRoleIds.filter((entry) => entry !== roleId && entry.trim().length > 0)
                        : skill.roles.filter((entry) => entry !== roleId);
                    if (rest.length === 0) {
                      // Nobody left to read it — an empty audience means «everyone»,
                      // so switch the skill off instead of round-tripping to «all».
                      save.mutate({ ref: skill.ref, name: skill.name, text: skill.text, enabled: false });
                      return;
                    }
                    scope.mutate({ ref: skill.ref, roles: rest });
                  }}
                />
                {skill.name}
                {!skill.enabled ? (
                  <span className="skill-scope">— off for every agent</span>
                ) : skill.roles.length === 0 ? (
                  <span className="skill-scope">— every agent</span>
                ) : null}
              </label>
              <span className="skill-actions">
                <Button
                  onClick={() => {
                    setOpenRef(open ? null : skill.ref);
                    setDraftText(skill.text);
                  }}
                >
                  {open ? 'Close' : 'Edit'}
                </Button>
                {skill.source === 'edited' ? (
                  <Button
                    aria-label={`delete ${skill.name}`}
                    onClick={() => remove.mutate(skill.ref)}
                  >
                    {skill.ref.startsWith('custom/') ? 'Delete' : 'Reset'}
                  </Button>
                ) : null}
              </span>
              {open ? (
                <div className="skill-edit">
                  <textarea rows={6} value={draftText} onChange={(event) => setDraftText(event.target.value)} />
                  <Button variant="primary"
                    disabled={draftText === skill.text || save.isPending}
                    onClick={() =>
                      save.mutate(
                        { ref: skill.ref, name: skill.name, text: draftText },
                        { onSuccess: () => setOpenRef(null) },
                      )
                    }
                  >
                    {save.isPending ? 'Saving…' : 'Save skill'}
                  </Button>
                </div>
              ) : null}
            </div>
          );
        })}
      </div>

      {scope.isError ? <p className="repo-probe repo-probe-bad">{String(scope.error).slice(0, 160)}</p> : null}
      {save.isError ? <p className="repo-probe repo-probe-bad">{String(save.error).slice(0, 160)}</p> : null}
      {remove.isError ? <p className="repo-probe repo-probe-bad">{String(remove.error).slice(0, 160)}</p> : null}

      {adding ? (
        <div className="skill-edit">
          <input value={newName} placeholder="skill name, e.g. release-notes" onChange={(event) => setNewName(event.target.value)} />
          <textarea rows={5} value={newText} placeholder="What this agent should know." onChange={(event) => setNewText(event.target.value)} />
          <div className="preset-row">
            <Button variant="primary"
              disabled={newName.trim().length === 0 || newText.trim().length === 0 || save.isPending}
              onClick={() => {
                const slug = newName.trim().toLowerCase().replace(/[^a-z0-9]+/gu, '-').replace(/^-+|-+$/gu, '');
                const ref = `custom/${slug}`;
                save.mutate(
                  { ref, name: newName.trim(), text: newText },
                  {
                    onSuccess: () => {
                      // Born scoped to its agent; untick later to share it.
                      scope.mutate({ ref, roles: [roleId] });
                      setAdding(false);
                      setNewName('');
                      setNewText('');
                    },
                  },
                );
              }}
            >
              {save.isPending ? 'Saving…' : 'Save skill'}
            </Button>
            <Button onClick={() => setAdding(false)}>
              Cancel
            </Button>
          </div>
        </div>
      ) : (
        <Button disabled={!usable} onClick={() => setAdding(true)}>
          + add a skill for this agent
        </Button>
      )}
    </Field>
  );
}


/**
 * The agent's credentials, on the agent (schema v10 grants, per-role view).
 *
 * The person's mental model is «у этого агента есть такие-то ключи» — so the
 * list lives on the card: tick a stored token to hand it to this agent, or
 * add a new one right here. Ticking applies immediately (grants live beside
 * the versioned document, not inside it), and the agent receives exactly the
 * ticked ones on its next start, as environment variables in
 * `.autonomous-team.env` inside its workspace. Values are never shown back —
 * the store keeps them; this card only says which names this agent holds.
 */
function RoleCredentials({
  roleId,
  credentials,
}: {
  roleId: string;
  credentials: readonly CredentialSummaryView[];
}): ReactNode {
  const [adding, setAdding] = useState(false);
  const [name, setName] = useState('');
  const [value, setValue] = useState('');

  const grant = useConsoleMutation<{ name: string; roles: readonly string[] }, unknown>(
    (data, context, args) => data.setCredentialRoles(args.name, args.roles, context),
    [['credentials']],
  );
  const put = useConsoleMutation<{ name: string; value: string }, unknown>(
    (data, context, args) => data.putCredential(args.name, { value: args.value, description: null, roles: [roleId] }, context),
    [['credentials']],
  );

  const usable = roleId.trim().length > 0;

  return (
    <Field
      label="Credentials"
      hint={
        usable
          ? 'Ticked tokens reach this agent on its next start, as variables in .autonomous-team.env inside its workspace. Values stay in the store (Tokens & access) and are never shown.'
          : 'Name the agent first — tokens are handed out by agent name.'
      }
    >
      <div className="editor-checks">
        {credentials.map((credential) => (
          <label key={credential.name} className="check">
            <input
              type="checkbox"
              disabled={!usable || grant.isPending}
              checked={credential.roles.includes(roleId)}
              onChange={(event) =>
                grant.mutate({
                  name: credential.name,
                  roles: event.target.checked
                    ? [...credential.roles, roleId]
                    : credential.roles.filter((entry) => entry !== roleId),
                })
              }
            />
            <code>{credential.name}</code>
            {credential.description === null || credential.description.length === 0 ? '' : ` — ${credential.description}`}
          </label>
        ))}
        {credentials.length === 0 && !adding ? <p className="hint">No tokens stored yet.</p> : null}
      </div>

      {adding ? (
        <div className="credential-add">
          <input
            style={{ flex: 1 }}
            value={name}
            placeholder="integrations/github"
            onChange={(event) => setName(event.target.value)}
          />
          <input
            style={{ flex: 1 }}
            type="text"
            value={value}
            placeholder="the token itself"
            onChange={(event) => setValue(event.target.value)}
          />
          <Button variant="primary"
            disabled={name.trim().length === 0 || value.length === 0 || put.isPending}
            onClick={() =>
              put.mutate(
                { name: name.trim(), value },
                {
                  onSuccess: () => {
                    setAdding(false);
                    setName('');
                    setValue('');
                  },
                },
              )
            }
          >
            {put.isPending ? 'Saving…' : 'Save token'}
          </Button>
          <Button onClick={() => setAdding(false)}>
            Cancel
          </Button>
        </div>
      ) : (
        <Button disabled={!usable} onClick={() => setAdding(true)}>
          + add a token for this agent
        </Button>
      )}
      {grant.isError ? <p className="repo-probe repo-probe-bad">{String(grant.error).slice(0, 160)}</p> : null}
      {put.isError ? <p className="repo-probe repo-probe-bad">{String(put.error).slice(0, 160)}</p> : null}
    </Field>
  );
}

/**
 * The repository template an agent of this stage gets when its files toggle
 * flips to "repository checkout". Reviewers get the read-only snapshot —
 * independence of review is the reason it exists (§9.1) — and everything else
 * gets the writable checkout. Falls back to whatever the document declares
 * first, because a made-up template name would fail compilation.
 */
/** `reviewer` → `reviewer-2`, `reviewer-2` → `reviewer-3`; never collides. */
function duplicateName(base: string, taken: readonly string[]): string {
  const stem = base.replace(/-\d+$/u, '') || 'agent';
  for (let n = 2; ; n += 1) {
    const candidate = `${stem}-${String(n)}`;
    if (!taken.includes(candidate)) return candidate;
  }
}

function IntegrationForm({
  integration,
  roles,
  credentials,
  onChange,
  onRemove,
}: {
  integration: IntegrationEdit;
  roles: readonly string[];
  credentials: readonly CredentialSummaryView[];
  onChange: (integration: IntegrationEdit) => void;
  onRemove: () => void;
}): ReactNode {
  const isMcp = integration.type === 'mcp';

  return (
    <article className="editor-card">
      <header className="editor-card-head">
        <input
          className="editor-title"
          value={integration.id}
          placeholder="connection name"
          onChange={(event) => onChange({ ...integration, id: event.target.value })}
        />
        <Button onClick={onRemove}>
          Remove
        </Button>
      </header>

      <div className="editor-grid">
        <Field label="Kind">
          <select
            value={integration.type}
            onChange={(event) => onChange({ ...integration, type: event.target.value as IntegrationEdit['type'] })}
          >
            <option value="mcp">MCP server</option>
            <option value="http">HTTP API</option>
            <option value="grafana">Grafana</option>
            <option value="custom">custom</option>
          </select>
        </Field>

        {/*
          Which stored token this connection uses, as a picker over what
          actually exists in Tokens & access. The value written into the
          document is still `secretref://<name>` — a reference, never a value —
          but nobody should have to know that spelling: choosing from the list
          makes the reference and the stored name agree by construction.
        */}
        <Field
          label="Credential"
          hint={
            credentials.length === 0
              ? 'Nothing is stored yet — add a token under Tokens & access first.'
              : 'The token itself lives under Tokens & access; the document holds only this reference.'
          }
        >
          {credentials.length > 0 &&
          (credentials.some((credential) => `secretref://${credential.name}` === integration.connectionRef) ||
            integration.connectionRef === 'secretref://integrations/' ||
            integration.connectionRef === '') ? (
            <select
              value={integration.connectionRef}
              onChange={(event) => onChange({ ...integration, connectionRef: event.target.value })}
            >
              {(integration.connectionRef === 'secretref://integrations/' || integration.connectionRef === '') && (
                <option value={integration.connectionRef}>choose a stored token…</option>
              )}
              {credentials.map((credential) => (
                <option key={credential.name} value={`secretref://${credential.name}`}>
                  {credential.name}
                  {credential.description === null || credential.description.length === 0 ? '' : ` — ${credential.description}`}
                </option>
              ))}
            </select>
          ) : (
            <input
              value={integration.connectionRef}
              onChange={(event) => onChange({ ...integration, connectionRef: event.target.value })}
            />
          )}
        </Field>

        {isMcp ? (
          <>
            <Field label="Remote URL" hint="For a hosted MCP server, for example https://mcp.trello.com/v1. Leave empty for a local command.">
              <input value={integration.url} onChange={(event) => onChange({ ...integration, url: event.target.value })} />
            </Field>
            <Field label="Command" hint="For a local MCP server, e.g. npx. Leave empty when using a remote URL.">
              <input value={integration.command} onChange={(event) => onChange({ ...integration, command: event.target.value })} />
            </Field>
            <Field label="Arguments" hint="One per line.">
              <textarea
                rows={3}
                value={integration.args.join('\n')}
                onChange={(event) => onChange({ ...integration, args: splitLines(event.target.value) })}
              />
            </Field>
          </>
        ) : null}

        <Field label="Hosts" hint="One per line. These become this agent's network allow-list.">
          <textarea
            rows={2}
            value={integration.endpoints.join('\n')}
            onChange={(event) => onChange({ ...integration, endpoints: splitLines(event.target.value) })}
          />
        </Field>

        {isMcp ? (
          <Field label="Environment" hint="NAME=secretref://… one per line. Values may be references.">
            <textarea
              rows={3}
              value={integration.env.map((entry) => `${entry.name}=${entry.value}`).join('\n')}
              onChange={(event) =>
                onChange({
                  ...integration,
                  env: splitLines(event.target.value).map((line) => {
                    const at = line.indexOf('=');
                    return at === -1
                      ? { name: line.trim(), value: '' }
                      : { name: line.slice(0, at).trim(), value: line.slice(at + 1).trim() };
                  }),
                })
              }
            />
          </Field>
        ) : null}
      </div>

      <fieldset className="editor-roles">
        <legend>Available to</legend>
        {roles.length === 0 ? (
          <p className="hint">Add an agent first.</p>
        ) : (
          roles.map((roleId) => (
            <label className="toggle-inline" key={roleId}>
              <input
                type="checkbox"
                checked={integration.roles.includes(roleId)}
                onChange={(event) =>
                  onChange({
                    ...integration,
                    roles: event.target.checked
                      ? [...integration.roles, roleId]
                      : integration.roles.filter((entry) => entry !== roleId),
                  })
                }
              />
              {roleId}
            </label>
          ))
        )}
      </fieldset>
    </article>
  );
}

/**
 * A column input with the board's names as suggestions. An input, not a
 * select, on purpose: new columns are created by typing them — there is no
 * other place where a column is born.
 */
function ColumnField({
  label,
  list,
  value,
  placeholder,
  hint,
  onChange,
}: {
  label: string;
  list: readonly string[];
  value: string;
  placeholder: string;
  hint?: string;
  onChange: (value: string) => void;
}): ReactNode {
  const listId = `columns-${label.replace(/[^a-z]/giu, '').toLowerCase()}`;
  return (
    <Field label={label} {...(hint === undefined ? {} : { hint })}>
      <input list={listId} value={value} placeholder={placeholder} onChange={(event) => onChange(event.target.value)} />
      <datalist id={listId}>
        {list.map((name) => (
          <option key={name} value={name} />
        ))}
      </datalist>
    </Field>
  );
}

/** The bare column input for inline rows; ColumnField wraps it in a Field. */
function ColumnInput({
  list,
  value,
  placeholder,
  onChange,
}: {
  list: readonly string[];
  value: string;
  placeholder: string;
  onChange: (value: string) => void;
}): ReactNode {
  const listId = 'columns-any';
  return (
    <>
      <input list={listId} style={{ flex: 1 }} value={value} placeholder={placeholder} onChange={(event) => onChange(event.target.value)} />
      <datalist id={listId}>
        {list.map((name) => (
          <option key={name} value={name} />
        ))}
      </datalist>
    </>
  );
}

/*
 * One Primer FormControl for every labelled field on this screen. The inner
 * control stays whatever the caller renders (TextInput, Select, textarea…);
 * the label/caption pairing and spacing are Primer's own.
 */
function Field({ label, hint, children }: { label: string; hint?: string; children: ReactNode }): ReactNode {
  return (
    <div className="editor-field">
      <span className="editor-label">{label}</span>
      {children}
      {hint === undefined ? null : <span className="editor-hint">{hint}</span>}
    </div>
  );
}

function splitLines(value: string): readonly string[] {
  return value
    .split('\n')
    .map((line) => line.trim())
    .filter((line) => line.length > 0);
}
